import { theme } from 'lib/theme';
import * as React from 'react';
import { LayoutChangeEvent, View, ViewProps } from 'react-native';
import { TextField } from './Field/TextField';
import { ICONS } from './Icon';
import { Glass } from './Glass';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { IsInHeaderProvider } from 'hooks/isInHeader';

type Props = {
  children?: React.ReactNode;
  leftChildren?: React.ReactNode | false;
  searchValue?: string;
  searchDisabled?: boolean;
  searchPlaceholder?: string;
  searchMaxLength?: number;
  searchOnSubmit?: boolean;
  searchFieldAbove?: boolean;
  searchFieldAboveFlexChildrenBelow?: boolean;
  onSearchEsc?: () => void;
  onLayout?: (event: LayoutChangeEvent) => void;
  onSearch?: ((value: string) => void) | false;
  style?: ViewProps['style'];
};

export const PageToolbar = ({
  children,
  leftChildren,
  searchDisabled,
  searchPlaceholder,
  searchMaxLength,
  searchValue,
  searchFieldAbove = false,
  searchOnSubmit,
  onSearch,
  onSearchEsc,
  onLayout,
  searchFieldAboveFlexChildrenBelow,
  style,
}: Props) => {
  const safeAreaInsets = useSafeAreaInsets();

  const SearchField = !!onSearch && (
    <Glass
      disabled={searchDisabled}
      style={{
        justifyContent: 'center',
        borderRadius: 99,
        flex: 1,
      }}
    >
      <TextField
        disabled={searchDisabled}
        value={searchValue}
        iconLeft={ICONS.MagnifyingGlassLight}
        onChange={onSearch}
        onEsc={onSearchEsc}
        enableManualOnChange={!!searchOnSubmit}
        maxLength={searchMaxLength}
        placeholder={searchPlaceholder}
        placeholderTextColor={theme.colors.fieldValueText}
        enterKeyHint="search"
        clearable
        autoCorrect={false}
        autoComplete="off"
        autoCapitalize="none"
        autoFocusWeb
        autoFocusDelay
        syncIncomingValue
        inputStyle={{
          minHeight: 44,
          backgroundColor: 'transparent',
        }}
        containerStyle={{
          paddingHorizontal: theme.paddingHorizontalHalf,
          borderRadius: 99,
          backgroundColor: 'transparent',
        }}
      />
    </Glass>
  );

  if (searchFieldAbove && !!onSearch) {
    return (
      <IsInHeaderProvider value={true}>
        <View
          style={{
            height: 44,
            marginBottom: theme.padding,
            paddingLeft: 16 + safeAreaInsets.left,
            paddingRight: 16 + safeAreaInsets.right,
          }}
          onLayout={onLayout}
        >
          {SearchField}
        </View>
        <View
          style={{
            height: 44,
            marginBottom: theme.padding,
            paddingLeft: 16 + safeAreaInsets.left,
            paddingRight: 16 + safeAreaInsets.right,
            flexDirection: 'row',
            alignItems: 'stretch',
            gap: theme.padding,
          }}
        >
          {leftChildren}
          {searchFieldAboveFlexChildrenBelow && (
            <View style={{ flex: 1 }}></View>
          )}
          {children}
        </View>
      </IsInHeaderProvider>
    );
  }

  return (
    <IsInHeaderProvider value={true}>
      <View
        style={[
          {
            height: 44,
            marginBottom: theme.padding,
            paddingLeft: 16 + safeAreaInsets.left,
            paddingRight: 16 + safeAreaInsets.right,
            flexDirection: 'row',
            alignItems: 'stretch',
            gap: theme.padding,
          },
          style,
        ]}
        onLayout={onLayout}
      >
        {leftChildren}
        {SearchField}
        {children}
      </View>
    </IsInHeaderProvider>
  );
};
